import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { resolveApiUrl } from '../services/api';
import logger from '../utils/logger';

function showToast(message: string, onClick?: () => void) {
  const el = document.createElement('div');
  el.style.cssText = `
    position: fixed; bottom: 24px; right: 24px; z-index: 9999;
    background: var(--accent, #059669); color: #fff;
    padding: 12px 20px; border-radius: 8px; font-size: 14px;
    box-shadow: 0 4px 12px rgba(0,0,0,.25); cursor: pointer;
    display: flex; align-items: center; gap: 10px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    max-width: 320px; line-height: 1.4;
  `;
  el.innerHTML = `<span style="font-size:18px">💬</span><span>${message}</span>`;
  if (onClick) el.addEventListener('click', onClick);
  el.addEventListener('click', () => el.remove());
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 6000);
}

export function useChatNotifications(onNavigateToMessages?: () => void): { unreadCount: number } {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const prevCountRef = useRef<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchUnread = useCallback(async () => {
    if (!user || user.role !== 'candidate') return;
    try {
      const res = await fetch(resolveApiUrl('/api/chat/unread'), {
        credentials: 'include',
      });
      if (!res.ok) return;
      const data = await res.json();
      const count: number = data?.data?.count ?? 0;
      setUnreadCount(count);

      if (prevCountRef.current !== null && count > prevCountRef.current) {
        const diff = count - prevCountRef.current;
        showToast(
          diff === 1 ? 'You have a new message' : `You have ${diff} new messages`,
          onNavigateToMessages
        );
      }
      prevCountRef.current = count;
    } catch (err) {
      logger.warn('Failed to fetch chat unread count:', err);
    }
  }, [user, onNavigateToMessages]);

  useEffect(() => {
    if (!user || user.role !== 'candidate') {
      setUnreadCount(0);
      return;
    }

    fetchUnread();
    intervalRef.current = setInterval(fetchUnread, 30_000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchUnread, user]);

  return { unreadCount };
}
